/**
 * Agent Assets Configuration
 *
 * Centralized configuration for all agent assets (banners and icons).
 * This replaces the need for individual banner/icon components for each agent.
 */

// Import all agent assets
import payoutsAgentIconUrl from '../assets/png/payouts-agent-icon.png';
import payoutsAgentBannerUrl from '../assets/png/payouts-agent-long.png';
import formulasAgentIconUrl from '../assets/png/formulas-agent-icon.png';
import formulasAgentBannerUrl from '../assets/png/formulas-agent-long.png';
import suiteAgentIconUrl from '../assets/png/suite-agent-icon.png';
import planDocsAgentBannerUrl from '../assets/png/plan-docs-agent-long.png';
import planDocsAgentsIconUrl from '../assets/png/plan-docs-agent-icon.png';

export interface AgentAssetConfig {
  /** Icon URL for the agent */
  icon: string;
  /** Banner URL for the agent */
  banner: string;
  /** Default alt text for accessibility */
  altText: string;
  /** Translation key for the agent name */
  translationKey: string;
  /** Default banner dimensions */
  defaultBannerSize?: {
    width: number | string;
    height: number | string;
  };
  /** Default icon dimensions */
  defaultIconSize?: {
    width: number | string;
    height: number | string;
  };
}

/**
 * Centralized agent asset configuration.
 * Maps agent kit names to their asset configuration.
 */
export const AGENT_ASSETS: Record<string, AgentAssetConfig> = {
  // Payouts agent
  'payouts.assistant.payouts_info_agent': {
    icon: payoutsAgentIconUrl,
    banner: payoutsAgentBannerUrl,
    altText: 'Payouts Information Agent',
    translationKey: 'agents.payouts.assistant.payouts_info_agent.name',
    defaultBannerSize: { width: '100%', height: 'auto' },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Comp Ops Agent
  'payouts.assistant.comp_ops_agent': {
    icon: payoutsAgentIconUrl,
    banner: payoutsAgentBannerUrl,
    altText: 'Comp Ops Agent',
    translationKey: 'agents.payouts.assistant.comp_ops_agent.name',
    defaultBannerSize: { width: '100%', height: 'auto' },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Comp Builder Agent
  'worksheets.comp_builder_agent': {
    icon: formulasAgentIconUrl,
    banner: formulasAgentBannerUrl,
    altText: 'Comp Builder Agent',
    translationKey: 'agents.comp_builder_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Inquiries agent - using formulas assets as fallback based on current components
  'disputes.inquiries': {
    icon: formulasAgentIconUrl, // Using formulas icon as fallback
    banner: formulasAgentBannerUrl, // Using formulas banner as fallback
    altText: 'Inquiries Agent',
    translationKey: 'agents.inquiries_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Plan document agent
  'planexecution.assistant.plan_document_agent': {
    icon: planDocsAgentsIconUrl,
    banner: planDocsAgentBannerUrl,
    altText: 'Plan Document Agent',
    translationKey: 'agents.plan_document_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Payee agent - using suite agent assets as fallback
  'payee.assistant.payee_agent': {
    icon: suiteAgentIconUrl,
    banner: planDocsAgentBannerUrl, // Using plan docs banner as fallback
    altText: 'Payee Agent',
    translationKey: 'agents.payee_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // What-If agent - using payout agent assets as fallback
  'payee.assistant.what_if_agent': {
    icon: payoutsAgentIconUrl,
    banner: payoutsAgentBannerUrl,
    altText: 'What-If scenario resolver',
    translationKey: 'agents.payee.assistant.what_if_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Transformation agent
  'worksheets.kits.transformation': {
    icon: formulasAgentIconUrl, // Using formulas icon as fallback
    banner: formulasAgentBannerUrl, // Using formulas banner as fallback
    altText: 'Transformation Agent',
    translationKey: 'agents.transformation_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Catalyst ML Training agent
  'catalyst.ml.training_run_explainer': {
    icon: formulasAgentIconUrl, // Using formulas icon as fallback
    banner: formulasAgentBannerUrl, // Using formulas banner as fallback
    altText: 'Catalyst ML Training Agent',
    translationKey: 'agents.catalyst_machine_learning_training_run_explainer_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Account Dealer agent
  'tq.account_dealer': {
    icon: suiteAgentIconUrl,
    banner: formulasAgentBannerUrl,
    altText: 'Account Dealer Agent',
    translationKey: 'agents.tq.account_dealer.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Revops agent
  'tq.revops_agent': {
    icon: suiteAgentIconUrl,
    banner: formulasAgentBannerUrl,
    altText: 'Revenue Planning Agent',
    translationKey: 'agents.tq.revops_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Catalyst EPM Agent
  'catalyst.epm.agent': {
    icon: planDocsAgentsIconUrl,
    banner: planDocsAgentBannerUrl,
    altText: 'Catalyst EPM Agent',
    translationKey: 'agents.catalyst.epm.agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // Catalyst ML Prediction agent
  'catalyst.ml.prediction_explainer': {
    icon: formulasAgentIconUrl, // Using formulas icon as fallback
    banner: formulasAgentBannerUrl, // Using formulas banner as fallback
    altText: 'Catalyst ML Prediction Agent',
    translationKey: 'agents.catalyst.ml.prediction_explainer.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  'audit.assistkit.audit_logs_agent': {
    icon: payoutsAgentIconUrl,
    banner: payoutsAgentBannerUrl,
    altText: 'Audit Logs Agent',
    translationKey: 'agents.audit.assistkit.audit_logs_agent.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },

  // EPM Financial Model Builder
  'epm.financial_model.builder': {
    icon: planDocsAgentsIconUrl,
    banner: planDocsAgentBannerUrl,
    altText: 'EPM Financial Model Builder',
    translationKey: 'agents.epm.financial_model.builder.name',
    defaultBannerSize: { width: 320, height: 120 },
    defaultIconSize: { width: 40, height: 40 },
  },
};

/**
 * Get asset configuration for an agent
 */
export function getAgentAssets(agentKitName: string): AgentAssetConfig | undefined {
  return AGENT_ASSETS[agentKitName];
}

/**
 * Get all available agent kit names with assets
 */
export function getAvailableAgentKits(): string[] {
  return Object.keys(AGENT_ASSETS);
}

/**
 * Check if an agent has asset configuration
 */
export function hasAgentAssets(agentKitName: string): boolean {
  return agentKitName in AGENT_ASSETS;
}
